import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';

export default function Contacts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({});
  const [search, setSearch] = useState('');

  const { data: list = [] } = useQuery<any[]>({
    queryKey: ['contacts', search],
    queryFn: async () => {
      const url = `/api/contacts${search ? `?search=${encodeURIComponent(search)}` : ''}`;
      return fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => r.json());
    },
  });
  const create = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contacts'] }); setOpen(false); setForm({}); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => fetch(`/api/contacts/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Contacts (Company Phonebook)</h1>
        <button onClick={() => { setForm({}); setOpen(true); }} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add</button>
      </div>
      <div className="flex items-center gap-2">
        <Search size={14} className="text-textSecondary" />
        <input className="input flex-1 max-w-md" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search name, company, email, phone…" />
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Company</th><th>Title</th><th>Email</th><th>Mobile</th><th>Source</th><th></th></tr></thead>
          <tbody>
            {list.map((c: any) => (
              <tr key={c.contact_uuid} className="hover:bg-elevated">
                <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td>{c.company || '—'}</td>
                <td>{c.title || '—'}</td>
                <td className="font-mono text-xs">{c.email || '—'}</td>
                <td className="font-mono">{c.mobile_phone || (c.business_phones || [])[0] || '—'}</td>
                <td className="text-xs text-textSecondary">{c.source}</td>
                <td><button onClick={() => remove.mutate(c.contact_uuid)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div className="p-6 text-center text-textSecondary">No contacts.</div>}
      </div>
      <Dialog open={open} onClose={() => setOpen(false)} title="Add Contact" size="md"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => create.mutate()} className="btn-primary">Save</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"  v={form.first_name}    set={(v: string) => setForm({ ...form, first_name: v })} />
          <Field label="Last name"   v={form.last_name}     set={(v: string) => setForm({ ...form, last_name: v })} />
          <Field label="Company"     v={form.company}       set={(v: string) => setForm({ ...form, company: v })} />
          <Field label="Title"       v={form.title}         set={(v: string) => setForm({ ...form, title: v })} />
          <Field label="Department"  v={form.department}    set={(v: string) => setForm({ ...form, department: v })} />
          <Field label="Email"       v={form.email}         set={(v: string) => setForm({ ...form, email: v })} type="email" />
          <Field label="Mobile"      v={form.mobile_phone}  set={(v: string) => setForm({ ...form, mobile_phone: v })} mono />
          <Field label="Address"     v={form.address}       set={(v: string) => setForm({ ...form, address: v })} />
        </div>
      </Dialog>
    </div>
  );
}

function Field({ label, v, set, mono = false, type = 'text' }: any) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <input className={`input ${mono ? 'font-mono' : ''}`} type={type} value={v || ''} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
